import { compareCompositeAgreement } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"

import type { Annotation } from "@/types/api/Annotation"

import type {
  GroupedSimulationData,
  HumanReviewData,
  MetricOutputData,
} from "./filterSimulations"

export interface ExperimentResult {
  value: string
  rawFloatValue?: number | null
  /**
   * Per-criterion results for composite metrics. Populated by the backend
   * experiment runner from result_json.individual_results — the source of
   * truth for per-criterion agreement (aggregate float is unsafe for that).
   */
  criteriaResults?: Array<{ result: string }>
  explanation: string
}

interface ExperimentAgreementStats {
  percentage: number
  agreements: number
  comparisons: number
  count: number
}

/**
 * Convert a HumanReviewData (UI shape) to an Annotation so
 * metric.annotationMatchesMetricOutput can dispatch to per-metric overrides.
 */
export const humanReviewToAnnotation = (
  review: HumanReviewData,
  simulationOutputId: string,
  metricId: string,
  priority: "PRIMARY" | "STANDARD" = "PRIMARY",
): Annotation =>
  ({
    id: review.id,
    simulation_output_id: simulationOutputId,
    assignee: review.reviewer || null,
    metric_id: metricId,
    ground_truth_float_value: review.float_value,
    ground_truth_string_value: review.string_value,
    ground_truth_set_value: review.set_value,
    ground_truth_json: null,
    ground_truth_subvalues_by_timestamp: null,
    annotations: null,
    archived: false,
    created_at: review.dateRaw || new Date().toISOString(),
    last_updated_at: review.dateRaw || new Date().toISOString(),
    priority,
  }) as unknown as Annotation

/**
 * Convert a raw experiment result value (LLM string output) to a metric-output
 * shape. Always populates string_value so binary metrics can resolve via
 * deriveYesNoString; float_value is populated only when the value is numeric.
 */
export const experimentValueToOutput = (
  value: string,
  outputType: string,
): MetricOutputData => {
  const parsed = parseFloat(value)
  return {
    output_type: outputType,
    float_value: Number.isFinite(parsed) ? parsed : null,
    string_value: value,
    set_value: outputType === "set" ? value.split(", ") : null,
    result_json: null,
  }
}

/**
 * Compute experiment agreement across all grouped simulations against the
 * PRIMARY human review. Skips sims without a result, ERROR results, sims
 * without a primary review, and reviews with no ground-truth value.
 * Iterates unfiltered groupedData so the stat is stable across table filters.
 *
 * For Composite metrics, criteria mode counts each paired criterion as one
 * comparison; score mode counts each comparable conversation as one unit.
 */
export const computeExperimentAgreement = (
  groupedData: GroupedSimulationData[],
  experimentResults: Record<string, ExperimentResult>,
  metric: MetricRecord,
  criteriaViewMode: "score" | "criteria" = "criteria",
): ExperimentAgreementStats | null => {
  const resultsCount = Object.keys(experimentResults).length
  if (resultsCount === 0) {
    return null
  }

  const isComposite = metric.metric_manager === "manager_composite_evaluation"

  let agreements = 0
  let comparisons = 0

  for (const data of groupedData) {
    const experimentResult = experimentResults[data.simulationOutputId]
    if (!experimentResult) {
      continue
    }
    if (experimentResult.value === "ERROR") {
      continue
    }

    const countedReview = data.humanReviews.find((r) => r.isCountedAsPrimary)
    if (!countedReview) {
      continue
    }

    const hasHumanValue =
      countedReview.float_value !== null ||
      countedReview.string_value !== null ||
      (countedReview.set_value !== null && countedReview.set_value.length > 0)
    if (!hasHumanValue) {
      continue
    }

    if (isComposite) {
      // Criteria mode counts paired criterion units; score mode counts one
      // normalized aggregate-score unit per comparable conversation.
      const outcome = compareCompositeAgreement(
        {
          float_value: experimentResult.rawFloatValue ?? null,
          set_value:
            experimentResult.criteriaResults?.map((c) => c.result) ?? null,
        },
        {
          float_value: countedReview.float_value,
          set_value: countedReview.set_value,
          json_value: countedReview.json_value ?? null,
        },
        metric.metric_metadata,
        criteriaViewMode,
      )
      if (outcome.kind === "skip") {
        continue
      }
      if (outcome.kind === "per-criterion") {
        agreements += outcome.matching
        comparisons += outcome.total
      } else {
        comparisons++
        if (outcome.agrees) {
          agreements++
        }
      }
      continue
    }

    comparisons++

    const annotation = humanReviewToAnnotation(
      countedReview,
      data.simulationOutputId,
      metric.id,
    )
    const experimentOutput = experimentValueToOutput(
      experimentResult.value,
      metric.output_type || "string",
    )
    // Use rawFloatValue when available so formatted display strings
    // (e.g. "Pass", "75%") don't corrupt the float comparison
    if (experimentResult.rawFloatValue != null) {
      experimentOutput.float_value = experimentResult.rawFloatValue
    }

    if (metric.annotationMatchesMetricOutput(annotation, experimentOutput)) {
      agreements++
    }
  }

  if (comparisons === 0) {
    return null
  }

  return {
    percentage: Math.round((agreements / comparisons) * 100),
    agreements,
    comparisons,
    count: resultsCount,
  }
}
